/**
 * v1.3.18 — Focus Mode Queue Drawer (Phase 2B).
 * v1.3.21 — Extended (Phase 3b) to also surface queued FIELD EDITS
 * alongside deferred AI jobs. Two independent lists share one drawer
 * so the assessor sees one unified "work I owe the network on
 * reconnect" panel. Each list has its own subscription + refresh
 * loop so a change in one never re-renders the other.
 *
 * Slide-out sheet listing:
 *   (a) every deferred AI job (from `deferredAiJobsQueue`)
 *   (b) every queued field edit (from `offlineFieldEditQueue`)
 *
 * Each row exposes two per-row actions: "Run Now" + "Discard".
 * ...
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, PlayCircle, Trash2, Plane, CheckCircle2, AlertTriangle, Clock, Pencil } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import {
  listDeferredJobs,
  removeDeferredJob,
  subscribeDeferredJobs,
  publishDeferredJobsChanged,
} from '../services/deferredAiJobsQueue';
import { runOneDeferredJob } from '../services/deferredAiJobsSync';
// v1.3.21 — offline field edits shown as a second list.
import {
  listFieldEdits,
  removeFieldEdit,
  subscribeFieldEdits,
  publishFieldEditsChanged,
} from '../services/offlineFieldEditQueue';
import { runOneFieldEdit } from '../services/offlineFieldEditSync';

const STATUS_STYLE = {
  pending:   { icon: Clock,          cls: 'text-slate-400',   label: 'Queued'    },
  running:   { icon: Loader2,        cls: 'text-sky-400 animate-spin', label: 'Running…' },
  completed: { icon: CheckCircle2,   cls: 'text-emerald-400', label: 'Done'      },
  failed:    { icon: AlertTriangle,  cls: 'text-amber-400',   label: 'Failed'    },
};

const _fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
};

export const FocusModeQueueDrawer = ({ open, onOpenChange }) => {
  const [jobs, setJobs] = useState([]);
  const [runningIds, setRunningIds] = useState(new Set());
  // v1.3.21 — separate state buckets for the field-edits list, so
  // a change in one queue never causes a re-render of the other.
  const [edits, setEdits] = useState([]);
  const [runningEditIds, setRunningEditIds] = useState(new Set());

  const refresh = useCallback(async () => {
    try {
      const rows = await listDeferredJobs();
      setJobs(rows);
    } catch (err) {
      console.error('[FocusModeQueueDrawer] refresh failed:', err);
    }
  }, []);

  const refreshEdits = useCallback(async () => {
    try {
      const rows = await listFieldEdits();
      setEdits(rows);
    } catch (err) {
      console.error('[FocusModeQueueDrawer] refreshEdits failed:', err);
    }
  }, []);

  useEffect(() => {
    if (open) { refresh(); refreshEdits(); }
  }, [open, refresh, refreshEdits]);

  useEffect(() => {
    // Live refresh from anywhere else in the app.
    const unsub = subscribeDeferredJobs(() => { refresh(); });
    return unsub;
  }, [refresh]);

  useEffect(() => {
    const unsub = subscribeFieldEdits(() => { refreshEdits(); });
    return unsub;
  }, [refreshEdits]);

  const handleRunNow = async (job) => {
    setRunningIds(prev => {
      const next = new Set(prev);
      next.add(job.id);
      return next;
    });
    try {
      const res = await runOneDeferredJob(job);
      if (res.ok) {
        toast.success(`Ran: ${job.label || job.job_type}`, { duration: 3500 });
      } else {
        toast.error(`Failed: ${job.label || job.job_type}`, { duration: 4500 });
      }
    } catch (err) {
      toast.error(`Failed: ${err?.message || 'unknown error'}`, { duration: 4500 });
    } finally {
      setRunningIds(prev => {
        const next = new Set(prev);
        next.delete(job.id);
        return next;
      });
      await refresh();
      publishDeferredJobsChanged();
    }
  };

  const handleDiscard = async (job) => {
    try {
      await removeDeferredJob(job.id);
      toast.info(`Discarded: ${job.label || job.job_type}`, { duration: 2500 });
      await refresh();
      publishDeferredJobsChanged();
    } catch (err) {
      toast.error(`Could not discard: ${err?.message || 'unknown'}`, { duration: 3500 });
    }
  };

  // v1.3.21 — field-edit variants of Run Now / Discard. Same code
  // shape as the AI-job handlers above; kept as separate functions
  // rather than merged with a `kind` param so the compiled call
  // graph stays trivially auditable (each queue drains through its
  // own module without any cross-talk).
  const handleRunEditNow = async (edit) => {
    setRunningEditIds(prev => { const next = new Set(prev); next.add(edit.id); return next; });
    try {
      const res = await runOneFieldEdit(edit);
      if (res.ok) toast.success(`Synced: ${edit.label || edit.kind}`, { duration: 3500 });
      else toast.error(`Failed: ${edit.label || edit.kind}`, { duration: 4500 });
    } catch (err) {
      toast.error(`Failed: ${err?.message || 'unknown error'}`, { duration: 4500 });
    } finally {
      setRunningEditIds(prev => { const next = new Set(prev); next.delete(edit.id); return next; });
      await refreshEdits();
      publishFieldEditsChanged();
    }
  };

  const handleDiscardEdit = async (edit) => {
    try {
      await removeFieldEdit(edit.id);
      toast.info(`Discarded: ${edit.label || edit.kind}`, { duration: 2500 });
      await refreshEdits();
      publishFieldEditsChanged();
    } catch (err) {
      toast.error(`Could not discard: ${err?.message || 'unknown'}`, { duration: 3500 });
    }
  };

  const pendingCount = jobs.filter(j => j.status === 'pending' || j.status === 'running').length;
  const pendingEditsCount = edits.filter(e => e.status === 'pending' || e.status === 'running').length;
  // Rough per-job ETA — 20s is a reasonable middle ground for the
  // 6 AI actions we defer (find-manuals ~15s, reanalyze ~25s,
  // site-review ~40s, others ~10s). Not committed to as an SLA,
  // just a communicative "batch will take about this long".
  const etaSec = pendingCount * 20;
  const etaLabel = etaSec < 60
    ? `${etaSec}s`
    : `${Math.round(etaSec / 60)}m`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="focus-mode-queue-drawer">
        <SheetHeader className="border-b border-slate-200 dark:border-slate-800 px-5 py-4">
          <SheetTitle className="flex items-center gap-2">
            <Plane size={16} className="rotate-45 text-sky-500" />
            Focus Mode Queue
          </SheetTitle>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 space-y-0.5">
            {pendingCount > 0
              ? <div data-testid="focus-mode-queue-eta">{pendingCount} AI task{pendingCount > 1 ? 's' : ''} pending · est. ~{etaLabel} to drain</div>
              : <div>No AI tasks queued.</div>}
            {pendingEditsCount > 0 && (
              <div data-testid="focus-mode-edits-eta">
                {pendingEditsCount} field edit{pendingEditsCount > 1 ? 's' : ''} pending — sync in insertion order on reconnect.
              </div>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-2 py-3 space-y-4" data-testid="focus-mode-queue-list">
          {/* ── Section 1: AI jobs ─────────────────────────────── */}
          <section>
            <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              AI tasks
            </div>
            {jobs.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-6 px-4">
                <Plane size={18} className="rotate-45 mx-auto mb-2 text-slate-400" />
                No AI tasks queued.
              </div>
            ) : (
              <div className="space-y-2">
                {jobs.map((job) => {
                  const style = STATUS_STYLE[job.status] || STATUS_STYLE.pending;
                  const Icon = style.icon;
                  const isRunning = runningIds.has(job.id) || job.status === 'running';
                  return (
                    <div
                      key={job.id}
                      data-testid={`focus-mode-queue-row-${job.id}`}
                      className="px-3 py-2.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                    >
                      <div className="flex items-start gap-2">
                        <Icon size={14} className={`mt-0.5 flex-shrink-0 ${style.cls}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                            {job.label || job.job_type}
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1.5">
                            <span>{style.label}</span>
                            <span>·</span>
                            <span>{_fmtTime(job.queued_at)}</span>
                            {(job.retry_count || 0) > 0 && (
                              <>
                                <span>·</span>
                                <span>retry {job.retry_count}</span>
                              </>
                            )}
                          </div>
                          {job.last_error && (
                            <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1 line-clamp-2" title={job.last_error}>
                              {job.last_error}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end gap-1.5 mt-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRunNow(job)}
                          disabled={isRunning || job.status === 'completed'}
                          data-testid={`focus-mode-queue-run-${job.id}`}
                          className="h-7 px-2 text-xs"
                        >
                          {isRunning ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                          <span className="ml-1">Run Now</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDiscard(job)}
                          disabled={isRunning}
                          data-testid={`focus-mode-queue-discard-${job.id}`}
                          className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                        >
                          <Trash2 size={12} />
                          <span className="ml-1">Discard</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ── Section 2: Field edits (v1.3.21) ────────────────── */}
          <section data-testid="focus-mode-edits-section">
            <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
              <Pencil size={11} /> Field edits
            </div>
            {edits.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-6 px-4">
                <Pencil size={16} className="mx-auto mb-2 text-slate-400" />
                No queued edits. Field saves you make while Focus Mode is on will appear here.
              </div>
            ) : (
              <div className="space-y-2">
                {edits.map((edit) => {
                  const style = STATUS_STYLE[edit.status] || STATUS_STYLE.pending;
                  const Icon = style.icon;
                  const isRunning = runningEditIds.has(edit.id) || edit.status === 'running';
                  return (
                    <div
                      key={edit.id}
                      data-testid={`focus-mode-edit-row-${edit.id}`}
                      className="px-3 py-2.5 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                    >
                      <div className="flex items-start gap-2">
                        <Icon size={14} className={`mt-0.5 flex-shrink-0 ${style.cls}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                            {edit.label || `${edit.kind} edit`}
                          </div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1.5">
                            <span>{style.label}</span>
                            <span>·</span>
                            <span>{_fmtTime(edit.queued_at)}</span>
                            {(edit.retry_count || 0) > 0 && (
                              <>
                                <span>·</span>
                                <span>retry {edit.retry_count}</span>
                              </>
                            )}
                          </div>
                          {edit.last_error && (
                            <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1 line-clamp-2" title={edit.last_error}>
                              {edit.last_error}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end gap-1.5 mt-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRunEditNow(edit)}
                          disabled={isRunning || edit.status === 'completed'}
                          data-testid={`focus-mode-edit-run-${edit.id}`}
                          className="h-7 px-2 text-xs"
                        >
                          {isRunning ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                          <span className="ml-1">Run Now</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDiscardEdit(edit)}
                          disabled={isRunning}
                          data-testid={`focus-mode-edit-discard-${edit.id}`}
                          className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                        >
                          <Trash2 size={12} />
                          <span className="ml-1">Discard</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FocusModeQueueDrawer;
